import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import Client from '@spike/client-model';
import { ResponseCard } from 'components/ClientIntakeForm/ResponseCard';
import { PetsResponses } from 'components/ClientIntakeForm/PetsResponses';
import Pet from '@spike/pet-model';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';

interface ClientIntakeFormProps {
    client: Client;
    pets: Array<Pet>;
    onBook: () => void;
    onNewSale?: () => void;
    onSelect: (key: string) => void;
    onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: '0px 16px 32px',
            fontFamily: 'Poppins',

            [theme.breakpoints.up('md')]: {
                padding: '48px 50px 80px 60px'
            }
        },
        header: {
            padding: '24px 16px 0px 16px',

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        title: {
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 600,
            marginTop: 0,
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                fontSize: 24
            }
        },
        filledDate: {
            margin: 0,
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 400,

            [theme.breakpoints.up('md')]: {
                fontSize: 15,
                marginBottom: 40
            }
        },
        grid: {
            'gap': 24,
            'marginTop': 40,
            'display': 'grid',
            'gridTemplateColumns': '1fr',

            [theme.breakpoints.up('md')]: {
                gridTemplateColumns: '300px 1fr'
            },

            '& > div': {
                minWidth: 0
            }
        },
        gridColumnInfo: {
            '& p': {
                margin: 0,
                fontSize: 14,
                lineHeight: 1.4,
                fontWeight: 400,

                [theme.breakpoints.up('md')]: {
                    fontSize: 15
                }
            }
        },
        responsesContainer: {
            gap: 16,
            display: 'flex',
            flexDirection: 'column'
        }
    })
);

export const ClientIntakeForm: React.FC<ClientIntakeFormProps> = props => {
    const classes = useStyles();

    const petsWithIntakeFormResponses = React.useMemo(() => {
        return props.pets.filter(pet => pet.customQuestionResponses.length > 0).length > 0;
    }, [props.pets]);

    const clientHasIntakeFormResponses = React.useMemo(() => {
        return props.client.customQuestionResponses.length > 0 || petsWithIntakeFormResponses;
    }, [props.client, petsWithIntakeFormResponses]);

    const filledDate = React.useMemo(() => {
        let date = null;

        if (props.client.customQuestionResponses.length > 0) {
            date = props.client.customQuestionResponses[0].createdAt;
        }

        if (!date && props.pets.some(pet => pet.customQuestionResponses.length > 0)) {
            date = props.pets.find(pet => pet.customQuestionResponses.length > 0)?.customQuestionResponses[0].createdAt;
        }

        return date?.format('MM/DD/YYYY');
    }, [props.client]);

    return (
        <>
            <div className={classes.header}>
                <ClientEditHeader
                    client={props.client}
                    onBook={props.onBook}
                    onNewSale={props.onNewSale}
                    onSelect={props.onSelect}
                    onClose={props.onClose}
                />
            </div>

            <div className={classes.root}>
                <h3 className={classes.title}>Intake Form</h3>
                <p className={classes.filledDate}>Filled on: {filledDate}</p>

                {clientHasIntakeFormResponses && (
                    <div className={classes.grid}>
                        <div className={classes.gridColumnInfo}>
                            <h4 className={classes.title}>Client Responses</h4>
                            <p>These are the responses the client provided about themselves in the intake form.</p>
                        </div>

                        <div className={classes.responsesContainer}>
                            {props.client.customQuestionResponses.map(response => (
                                <ResponseCard
                                    key={response.id}
                                    response={response}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {petsWithIntakeFormResponses && (
                    <div className={classes.grid}>
                        <div className={classes.gridColumnInfo}>
                            <h4 className={classes.title}>Pet Responses</h4>
                            <p>These are the responses the client provided about their pets in the intake form.</p>
                        </div>

                        <PetsResponses pets={props.pets} />
                    </div>
                )}
            </div>
        </>
    );
};
